import { Button, useAlert } from '@ads/components-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useConsolidationYears } from '@/features/individual-payment/hooks/useConsolidationYears';
import { useIndividualPaymentHeader } from '@/features/individual-payment/hooks/useIndividualPaymentHeader';
import { useIndividualPaymentParcels } from '@/features/individual-payment/hooks/useIndividualPaymentParcels';
import { usePersonPlans } from '@/features/individual-payment/hooks/usePersonPlans';
import { ConsolidationEmptyState } from '@/features/individual-payment/ui/molecules/ConsolidationEmptyState';
import type { ConsolidationPlanOption } from '@/features/individual-payment/ui/molecules/ConsolidationHeaderCard';
import { ConsolidationHeaderCard } from '@/features/individual-payment/ui/molecules/ConsolidationHeaderCard';
import { ConsolidationStatCard } from '@/features/individual-payment/ui/molecules/ConsolidationStatCard';
import { ConsolidationTable } from '@/features/individual-payment/ui/organisms/ConsolidationTable';
import { ConsolidationTemplate } from '@/features/individual-payment/ui/templates/ConsolidationTemplate';
import { deriveConsolidation } from '@/features/individual-payment/utils/deriveConsolidation';
import { EMPTY_VALUE, formatCurrency } from '@/features/individual-payment/utils/formatCurrency';
import { formatPlanLabel } from '@/features/individual-payment/utils/formatPlanLabel';
import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

/**
 * O ano do recorte vive na search param, não em estado local: é ele que define
 * o conteúdo da tela, então com ele na URL o link colado no chat abre o mesmo
 * demonstrativo. `optional` porque o estado inicial é justamente "sem ano".
 */
const searchSchema = z.object({
  year: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute(
  '/_authenticated/individual-payment/$financialPersonId/consolidation/',
)({
  validateSearch: searchSchema,
  component: IndividualPaymentConsolidationPage,
});

/**
 * Página de consolidação anual de um plano de pagamento.
 *
 * Somente leitura. Orquestra as três requisições — header (identificação do
 * aluno e total de parcelas do contrato), parcelas (a coleção completa, de onde
 * saem os anos e todos os agregados) e cursos do aluno (as opções do seletor de
 * plano) — e traduz a escolha dos seletores em navegação, para que a URL
 * continue descrevendo o que está na tela.
 *
 * Não há endpoint de consolidação no backend: `deriveConsolidation` é quem faz
 * o recorte por ano e os agregados. Quando o endpoint existir, ele é o único
 * ponto a substituir.
 */
export function IndividualPaymentConsolidationPage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { financialPersonId: rawId } = Route.useParams();
  const { year: selectedYear } = Route.useSearch();

  // O parâmetro de rota é string. Converter aqui, na fronteira, evita propagar
  // `NaN` para a URL de duas requisições.
  const financialPersonId = useMemo(() => {
    const parsed = Number(rawId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [rawId]);

  const { header, isLoading: isHeaderLoading } = useIndividualPaymentHeader(financialPersonId);

  const {
    parcels,
    isLoading: areParcelsLoading,
    isError: areParcelsError,
    refetch: refetchParcels,
  } = useIndividualPaymentParcels(financialPersonId);

  const { plans, isLoading: arePlansLoading } = usePersonPlans(header?.personId ?? null);

  const years = useConsolidationYears(parcels);

  const summary = useMemo(
    () => deriveConsolidation(parcels, selectedYear ?? null),
    [parcels, selectedYear],
  );

  /**
   * Opções do seletor de plano.
   *
   * O plano da rota entra na lista mesmo quando os cursos ainda não chegaram —
   * ou falharam: sem ele o `SelectInput` não teria valor correspondente e o
   * campo apareceria vazio, sugerindo que nenhum plano está selecionado.
   */
  const planOptions = useMemo<readonly ConsolidationPlanOption[]>(() => {
    const fallbackLabel = (id: number) => t('consolidation.plan.unnamed', { id });

    const fromApi = plans
      .filter((plan): plan is typeof plan & { id: number } => plan.id != null)
      .map(plan => ({ id: plan.id, label: formatPlanLabel(plan, fallbackLabel(plan.id)) }));

    if (financialPersonId === null || fromApi.some(option => option.id === financialPersonId)) {
      return fromApi;
    }

    return [
      {
        id: financialPersonId,
        label: header?.courseName ?? fallbackLabel(financialPersonId),
      },
      ...fromApi,
    ];
  }, [plans, financialPersonId, header?.courseName, t]);

  /**
   * Ano a exibir, ou `null` para o estado inicial.
   *
   * Durante o carregamento das parcelas a lista de anos ainda está vazia, então
   * `years.includes` seria falso e quem abrisse a URL com `?year=2023` veria o
   * estado inicial piscar antes da tabela. Enquanto carrega, a palavra do
   * `year` da URL vale; a checagem contra `years` só entra depois da resposta.
   */
  const displayYear =
    selectedYear !== undefined && (areParcelsLoading || years.includes(selectedYear))
      ? selectedYear
      : null;

  const handlePlanChange = (planId: number) => {
    if (planId === financialPersonId) return;

    // O ano é levado junto na esperança de o novo plano também tê-lo — as
    // parcelas dele ainda não chegaram, então aqui não dá para saber. Se não
    // tiver, o efeito abaixo o remove da URL assim que a resposta chega.
    void navigate({
      to: '/individual-payment/$financialPersonId/consolidation',
      params: { financialPersonId: String(planId) },
      search: selectedYear === undefined ? {} : { year: selectedYear },
    });
  };

  // Reconciliação da URL, não busca de dados: quando o plano recém-escolhido não
  // tem parcelas no ano que veio junto, o `year` fica órfão. A tela já se comporta
  // certo (`hasYear` é falso e o estado inicial aparece), mas a URL continuaria
  // afirmando um recorte que não existe — e é ela que o operador copia e
  // compartilha. `replace` para não criar uma volta ao estado inconsistente.
  useEffect(() => {
    if (selectedYear === undefined || areParcelsLoading || areParcelsError) return;
    if (years.includes(selectedYear)) return;

    void navigate({
      to: '/individual-payment/$financialPersonId/consolidation',
      params: { financialPersonId: rawId },
      search: {},
      replace: true,
    });
  }, [selectedYear, years, areParcelsLoading, areParcelsError, navigate, rawId]);

  const handleYearChange = (year: number) => {
    // `replace`: percorrer cinco anos de um plano não deve empilhar cinco
    // entradas de histórico entre o operador e a tela de onde ele veio.
    void navigate({
      to: '/individual-payment/$financialPersonId/consolidation',
      params: { financialPersonId: rawId },
      search: { year },
      replace: true,
    });
  };

  const handleDownloadPdf = (parcels: readonly IndividualPaymentParcelViewModel[]) => {
    // Não existe endpoint de PDF consolidado em /api/v1/individualpayment/* —
    // só `parcels/{parcelId}/serials`, com o `pdfFileUrl` de um boleto. A
    // mensagem é específica para o operador não achar que é falta de permissão,
    // e cita a contagem para deixar claro que a seleção foi de fato registrada.
    alert({
      alertType: 'warning',
      title: t('consolidation.pdfUnavailable', { count: parcels.length }),
    });
  };

  if (financialPersonId === null) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('consolidation.invalidId')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('consolidation.invalidIdHint')}
        </p>
        <Button
          hierarchy="secondary"
          size="md"
          onClick={() => void navigate({ to: '/individual-payment' })}
        >
          {t('consolidation.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <ConsolidationTemplate studentName={header?.studentName ?? EMPTY_VALUE}>
      <ConsolidationHeaderCard
        studentName={header?.studentName}
        email={header?.email}
        isStudentLoading={isHeaderLoading}
        plans={planOptions}
        selectedPlanId={financialPersonId}
        isPlanSelectDisabled={arePlansLoading || planOptions.length <= 1}
        onPlanChange={handlePlanChange}
        years={years}
        selectedYear={displayYear}
        onYearChange={handleYearChange}
      />

      {displayYear !== null ? (
        <>
          <div className="flex flex-wrap items-center rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-xxs">
            <ConsolidationStatCard
              label={t('consolidation.stats.totalYear')}
              value={formatCurrency(summary.expectedValue)}
              hint={t('consolidation.stats.totalYearHint', {
                count: summary.total,
                year: displayYear,
              })}
              isLoading={areParcelsLoading}
            />
            <div className="h-md w-px bg-ctx-layout-border" />
            <ConsolidationStatCard
              label={t('consolidation.stats.totalPaid')}
              value={formatCurrency(summary.paidValue)}
              hint={t('consolidation.stats.totalPaidHint', { count: summary.paid })}
              isLoading={areParcelsLoading}
            />
            <div className="h-md w-px bg-ctx-layout-border" />
            <ConsolidationStatCard
              label={t('consolidation.stats.open')}
              value={formatCurrency(summary.openValue)}
              hint={t('consolidation.stats.openHint', { count: summary.open })}
              isLoading={areParcelsLoading}
            />
            <div className="h-md w-px bg-ctx-layout-border" />
            <ConsolidationStatCard
              label={t('consolidation.stats.discounts')}
              value={formatCurrency(summary.discountValue)}
              hint={
                summary.discountValue > 0
                  ? t('consolidation.stats.discountsHint', { count: summary.discountedCount })
                  : t('consolidation.stats.noDiscount')
              }
              isLoading={areParcelsLoading}
            />
          </div>

          {/* `key` amarra a seleção ao recorte: trocar de ano ou de plano
              descarta as marcações, que apontariam para parcelas fora da tela. */}
          <ConsolidationTable
            key={`${financialPersonId}-${displayYear}`}
            summary={summary}
            year={displayYear}
            isLoading={areParcelsLoading}
            isError={areParcelsError}
            onRetry={() => void refetchParcels()}
            onDownloadPdf={handleDownloadPdf}
          />
        </>
      ) : (
        <ConsolidationEmptyState />
      )}
    </ConsolidationTemplate>
  );
}
